import axios from 'axios';
import { Module, ActionTree, MutationTree, GetterTree } from 'vuex';
import {
  User,
  ExporoUser,
  MangoPayUser,
  ProfileState,
  IdToken,
} from '@/store/profile/types';
import { RootState } from '@/store/types';
import { identifySentryUser } from '@/plugins/sentry';
import { getIdToken } from '@/utils/Auth';
import LocalStorage from '@/utils/LocalStorage';
import { removeEmptyKeys } from '@/utils/cleanObjects';
import mapExporoMangoUser from '@/utils/mapExporoMangoUser';
import parseJWT from '@/utils/parseJWT';
import { gtmTrack, gtmActions } from '@/utils/gtm';

const state: ProfileState = {
  user: null,
  exporoUser: null,
  mangoPayUser: null,
  idToken: null,
};

const mutations: MutationTree<ProfileState> = {
  SET_USER(state: ProfileState, user: User) {
    state.user = user;
  },
  SET_EXPORO_USER(state: ProfileState, exporoUser: ExporoUser) {
    state.exporoUser = exporoUser;
  },
  SET_MANGOPAY_USER(state: ProfileState, mangoPayUser: MangoPayUser) {
    state.mangoPayUser = mangoPayUser;
  },
  SET_ID_TOKEN(state: ProfileState, idToken: IdToken) {
    state.idToken = idToken;
  },
};

const getters: GetterTree<ProfileState, RootState> = {
  user: (state: ProfileState) => state.user,
  exporoUser: (state: ProfileState) => state.exporoUser,
  mangoPayUser: (state: ProfileState) => state.mangoPayUser,

  exporoId: (state: ProfileState): string => {
    const { idToken } = state;
    const id = (idToken && idToken.user_id) || '';

    return `${id}`;
  },

  fullName(state: ProfileState): string {
    const { exporoUser } = state;
    if (!exporoUser) {
      return '';
    }

    const { naturalUser } = exporoUser;
    const firstName = naturalUser.firstName;
    const lastName = naturalUser.lastName;

    return `${firstName} ${lastName}`;
  },
};

const actions: ActionTree<ProfileState, RootState> = {
  fetchIdToken({ commit }: { commit: Function }) {
    const idToken = getIdToken();
    if (idToken) {
      const parsedToken = parseJWT(idToken);
      commit('SET_ID_TOKEN', parsedToken);
    }
  },

  validateUser({ commit, dispatch }: { commit: Function; dispatch: Function }) {
    return axios
      .get(`${process.env.VUE_APP_EXPORO_USER_API}/user/metaData`)
      .then(({ data }) => {
        commit('SET_EXPORO_USER', data);

        return dispatch('fetchUser');
      })
      .catch((error) => {
        commit('SET_EXPORO_USER', null);
        const err = error.response ? error.response : error;

        return Promise.reject(err);
      });
  },

  fetchUser(
    { commit, dispatch }: { commit: Function; dispatch: Function },
    exporoId: number
  ) {
    return axios
      .get(`${process.env.VUE_APP_EXPORO_WALLET_API_URL}/user/current-user`)
      .then(async (response) => {
        const { mangoPayUser, internalUser } = response.data;
        identifySentryUser(internalUser, mangoPayUser);

        // Add tracking action to Google tag Manager dataLayer
        const trackData = { userId: state.idToken.user_id };
        gtmTrack(gtmActions.USER_AUTH_SUCCESSFUL, trackData);

        commit('SET_USER', internalUser);
        commit('SET_MANGOPAY_USER', mangoPayUser);

        await dispatch('wallet/fetchUserWallets', {}, { root: true });
        dispatch('wallet/fetchTransactions', {}, { root: true });

        return Promise.resolve(response.data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  createUser({
    commit,
    state,
  }: {
    commit: Function;
    state: ProfileState;
    dispatch: Function;
  }) {
    if (!state.exporoUser) {
      return Promise.reject('no profile.exporoUser found in store');
    }
    const user = mapExporoMangoUser(state.exporoUser, state.idToken);

    const redirect = LocalStorage.has('CREATE_WALLET_REDIRECT');

    return axios
      .post(
        `${process.env.VUE_APP_EXPORO_WALLET_API_URL}/user`,
        removeEmptyKeys(user)
      )
      .then((response) => {
        if (redirect) {
          const redirectURL = LocalStorage.get('CREATE_WALLET_REDIRECT');

          LocalStorage.remove('CREATE_WALLET_REDIRECT');
          window.location.href = `${redirectURL}`;
        }

        commit('SET_USER', response.data);

        return Promise.resolve(response.data);
      })
      .catch((error) => {
        commit('SET_USER', undefined);

        return Promise.reject(error.response);
      });
  },

  signOut() {
    const request = (path: string, params: any, method: string = 'post') => {
      const form = document.createElement('form');
      form.method = method;
      form.action = path;
      for (const key in params) {
        const hiddenField = document.createElement('input');
        hiddenField.type = 'hidden';
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
    };
    const payload = {
      redirectURL: `${window.location.origin}${window.location.pathname}`,
    };

    return request(
      `${process.env.VUE_APP_EXPORO_USER_API}/user/session/flush`,
      payload
    );
  },
};

export const profile: Module<ProfileState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
